import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, ChevronLeft, ChevronRight, CalendarDays, Loader2, Search } from 'lucide-react';
import { UserService } from '../services/apiService';
import { User, UserRole } from '../types';
import { useToast } from './ToastContext';

// Agenda settimanale dei collaboratori: righe = utenti, colonne = Lun-Dom,
// con barre colorate (blu incarichi, verde turni fiduciari, grigio incarichi
// senza orario). Il click su una barra apre il dettaglio pratica/servizio.
// L'agenda di ogni utente visibile si carica in parallelo.

type BarType = 'investigazione' | 'fiduciario' | 'all-day';

type Bar = {
    type: BarType;
    startMinutes: number;
    endMinutes: number;
    label: string;
    navTarget: string | null;
};

type DayBars = Record<string /* YYYY-MM-DD */, Bar[]>;
type AgendaPerUser = Record<number /* userId */, DayBars>;

const DAY_NAMES = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];
const HOUR_START = 6;
const HOUR_END = 24;
const HOUR_TOTAL = HOUR_END - HOUR_START;

// ── Helpers data/ora ────────────────────────────────────────────────────────

const toIsoDate = (d: Date): string => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
};

const startOfWeek = (d: Date): Date => {
    const nd = new Date(d);
    const dow = (nd.getDay() + 6) % 7; // 0 = lunedì
    nd.setDate(nd.getDate() - dow);
    nd.setHours(0, 0, 0, 0);
    return nd;
};

const addDays = (d: Date, n: number): Date => {
    const nd = new Date(d);
    nd.setDate(nd.getDate() + n);
    return nd;
};

const parseLocalTime = (t: string | null | undefined): number => {
    if (!t) return 0;
    const parts = t.split(':');
    return (parseInt(parts[0], 10) || 0) * 60 + (parseInt(parts[1] || '0', 10) || 0);
};

const parseLocalDate = (iso: string | null | undefined): string | null => {
    if (!iso) return null;
    return iso.split('T')[0];
};

const fmtTime = (t: string | null | undefined): string => (t || '').slice(0, 5);

// ── Aggregazione agenda → barre per giorno ──────────────────────────────────

const processAgenda = (agendaResp: any, weekDates: string[]): DayBars => {
    const out: DayBars = {};
    weekDates.forEach(d => { out[d] = []; });
    if (!agendaResp) return out;

    // Incarichi investigativi (blu con orari, grigio all-day). Esclusi revocati,
    // rifiutati, quelli su mandati eliminati e gli autogenerati (contenitori dei file
    // caricati dal File Manager): non sono impegni reali.
    (agendaResp.incarichi || []).forEach((inc: any) => {
        if (inc.stato === 'REVOCATO' || inc.stato === 'RIFIUTATO') return;
        if (inc.tipoOrigine === 'AUTOGENERATO') return;
        if (inc.praticaDeleted) return;
        const relazioniDates = new Set<string>();
        (inc.relazioni || []).forEach((r: any) => {
            const d = parseLocalDate(r.dataRelazione);
            if (!d || !weekDates.includes(d)) return;
            relazioniDates.add(d);
            const oi = r.orarioInizio, of = r.orarioFine;
            if (!oi || !of) return;
            out[d].push({
                type: 'investigazione',
                startMinutes: parseLocalTime(oi),
                endMinutes: parseLocalTime(of),
                label: `${inc.praticaNome || 'Incarico'} • ${fmtTime(oi)}-${fmtTime(of)}`,
                navTarget: inc.praticaId && !inc.praticaDeleted ? `/pratiche/${inc.praticaId}` : null,
            });
        });
        // Copertura "all-day" per i giorni dell'incarico senza relazione specifica
        const di = parseLocalDate(inc.dataInizio);
        const df = parseLocalDate(inc.dataFine) || di;
        if (!di || !df) return;
        const start = new Date(di); start.setHours(0, 0, 0, 0);
        const end = new Date(df); end.setHours(0, 0, 0, 0);
        const cursor = new Date(start);
        while (cursor <= end) {
            const iso = toIsoDate(cursor);
            if (weekDates.includes(iso) && !relazioniDates.has(iso)) {
                out[iso].push({
                    type: 'all-day',
                    startMinutes: HOUR_START * 60,
                    endMinutes: HOUR_END * 60,
                    label: `${inc.praticaNome || 'Incarico'} • giorno intero`,
                    navTarget: inc.praticaId && !inc.praticaDeleted ? `/pratiche/${inc.praticaId}` : null,
                });
            }
            cursor.setDate(cursor.getDate() + 1);
        }
    });

    // Turni fiduciari (verde). Il notturno diventa due barre: fino a mezzanotte e dal mattino dopo.
    // Esclusi i turni rifiutati e revocati, come per gli incarichi.
    (agendaResp.turni || []).forEach((t: any) => {
        if (t.statoAccettazione === 'RIFIUTATO') return;
        if (t.dataRevoca) return;
        if (!t.dataTurno || !t.oraInizio || !t.oraFine) return;
        const oi = parseLocalTime(t.oraInizio);
        const of = parseLocalTime(t.oraFine);
        const baseLabel = `${t.servizio?.nome || 'Turno'} • ${fmtTime(t.oraInizio)}-${fmtTime(t.oraFine)}`;
        const navTarget = t.servizio?.id ? `/servizi/${t.servizio.id}` : null;

        if (t.notturno && of <= oi) {
            if (weekDates.includes(t.dataTurno)) {
                out[t.dataTurno].push({
                    type: 'fiduciario', startMinutes: oi, endMinutes: 24 * 60, label: baseLabel, navTarget,
                });
            }
            const nextIso = toIsoDate(addDays(new Date(t.dataTurno), 1));
            if (weekDates.includes(nextIso)) {
                out[nextIso].push({
                    type: 'fiduciario', startMinutes: 0, endMinutes: of, label: `${baseLabel} (notturno)`, navTarget,
                });
            }
        } else if (weekDates.includes(t.dataTurno)) {
            out[t.dataTurno].push({
                type: 'fiduciario', startMinutes: oi, endMinutes: of, label: baseLabel, navTarget,
            });
        }
    });

    return out;
};

// ── Cella giorno con timeline verticale ─────────────────────────────────────

const DayCell: React.FC<{ bars: Bar[]; onNavigate: (path: string) => void }> = ({ bars, onNavigate }) => (
    <div className="relative h-24 border-l border-slate-100 bg-slate-50/40">
        {/* Linee orarie di riferimento (08, 12, 18) per orientarsi visivamente */}
        {[8, 12, 18].map(h => (
            <div key={h}
                className="absolute left-0 right-0 border-t border-dashed border-slate-200/80"
                style={{ top: `${((h - HOUR_START) / HOUR_TOTAL) * 100}%` }}
            />
        ))}
        {bars.map((b, i) => {
            const startInRange = Math.max(b.startMinutes, HOUR_START * 60);
            const endInRange = Math.min(b.endMinutes, HOUR_END * 60);
            if (endInRange <= startInRange) return null;
            const topPct = ((startInRange - HOUR_START * 60) / (HOUR_TOTAL * 60)) * 100;
            const heightPct = Math.max(((endInRange - startInRange) / (HOUR_TOTAL * 60)) * 100, 3);
            const color = b.type === 'investigazione'
                ? 'bg-indigo-500/85 border-indigo-700 hover:bg-indigo-600'
                : b.type === 'fiduciario'
                    ? 'bg-emerald-500/85 border-emerald-700 hover:bg-emerald-600'
                    : 'bg-slate-300/70 border-slate-400 hover:bg-slate-400/80';
            const clickable = !!b.navTarget;
            return (
                <button
                    key={i}
                    type="button"
                    title={b.label}
                    disabled={!clickable}
                    onClick={() => { if (b.navTarget) onNavigate(b.navTarget); }}
                    className={`absolute left-0.5 right-0.5 ${color} border-l-2 rounded-sm transition-all ${clickable ? 'cursor-pointer' : 'cursor-default'}`}
                    style={{ top: `${topPct}%`, height: `${heightPct}%` }}
                />
            );
        })}
    </div>
);

// ── Componente principale ───────────────────────────────────────────────────

type Props = {
    users: User[];   // già filtrati dal BE per ruolo del chiamante
    onClose: () => void;
    // Se valorizzata ("YYYY-MM-DD" o ISO), apre la settimana che contiene la data,
    // es. dal form di assegnazione per vedere chi è libero.
    initialDate?: string;
    // Quando fornita, ogni riga collaboratore mostra un pulsante "Seleziona":
    // il chiamante usa il callback per riempire il proprio form e chiudere.
    onSelectCollaboratore?: (userId: number) => void;
};

const AgendaSettimanaleModal: React.FC<Props> = ({ users, onClose, initialDate, onSelectCollaboratore }) => {
    const navigate = useNavigate();
    const { addToast } = useToast();
    const [weekStart, setWeekStart] = useState<Date>(() => {
        if (initialDate) {
            const dateStr = initialDate.split('T')[0];
            const parsed = new Date(`${dateStr}T00:00:00`);
            if (!isNaN(parsed.getTime())) return startOfWeek(parsed);
        }
        return startOfWeek(new Date());
    });
    const [loading, setLoading] = useState(false);
    const [agendaByUser, setAgendaByUser] = useState<AgendaPerUser>({});
    const [search, setSearch] = useState('');

    // Tutti gli utenti restituiti dal BE, che filtra già per ruolo del chiamante.
    // Si includono anche titolare e responsabili: possono avere incarichi assegnati.
    const collabIdsToFetch = useMemo(
        () => users.map(u => u.id),
        [users]
    );

    // Lista filtrata dalla ricerca, ordinata per ruolo (collaboratori,
    // responsabili, admin) e poi per cognome.
    const roleOrder: Record<string, number> = {
        [UserRole.COLLABORATORE]: 0,
        [UserRole.RESPONSABILE]: 1,
        [UserRole.ADMIN]: 2,
    };
    const collaboratori = useMemo(
        () => users
            .filter(u => {
                if (!search.trim()) return true;
                const s = search.toLowerCase();
                return (u.name || '').toLowerCase().includes(s) || (u.surname || '').toLowerCase().includes(s);
            })
            .sort((a, b) => {
                const ra = roleOrder[a.role] ?? 99;
                const rb = roleOrder[b.role] ?? 99;
                if (ra !== rb) return ra - rb;
                return (a.surname || a.name || '').localeCompare(b.surname || b.name || '');
            }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [users, search]
    );

    const weekDates = useMemo(
        () => Array.from({ length: 7 }, (_, i) => toIsoDate(addDays(weekStart, i))),
        [weekStart]
    );

    useEffect(() => {
        let cancelled = false;
        if (collabIdsToFetch.length === 0) { setAgendaByUser({}); return; }
        const from = weekDates[0];
        const to = weekDates[6];
        setLoading(true);
        Promise.all(collabIdsToFetch.map(id =>
            UserService.getAgenda(id, from, to)
                .then(data => ({ id, data }))
                .catch(() => ({ id, data: null }))
        )).then(results => {
            if (cancelled) return;
            const out: AgendaPerUser = {};
            results.forEach(({ id, data }) => { out[id] = processAgenda(data, weekDates); });
            setAgendaByUser(out);
        }).catch(() => {
            if (!cancelled) addToast("Errore nel caricamento dell'agenda settimanale", 'error');
        }).finally(() => {
            if (!cancelled) setLoading(false);
        });
        return () => { cancelled = true; };
    }, [collabIdsToFetch, weekDates, addToast]);

    const goNavigate = (path: string) => { onClose(); navigate(path); };

    const weekLabel = useMemo(() => {
        const last = addDays(weekStart, 6);
        const fmt = (d: Date) => `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`;
        return `${fmt(weekStart)} – ${fmt(last)}/${last.getFullYear()}`;
    }, [weekStart]);

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-7xl max-h-[92vh] flex flex-col overflow-hidden">
                {/* Header */}
                <div className="p-5 border-b border-gray-100 bg-gray-50 flex justify-between items-center flex-wrap gap-3 flex-shrink-0">
                    <div className="flex items-center gap-3">
                        <CalendarDays size={22} className="text-blue-600" />
                        <div>
                            <h2 className="text-lg font-bold text-gray-900">Agenda Settimanale Collaboratori</h2>
                            <p className="text-xs text-gray-500">Orari pianificati. Click su una barra per aprire la pratica/servizio.</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <button onClick={() => setWeekStart(addDays(weekStart, -7))}
                            className="p-2 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                            title="Settimana precedente"><ChevronLeft size={18} /></button>
                        <span className="text-sm font-bold text-gray-700 px-2 min-w-[180px] text-center">{weekLabel}</span>
                        <button onClick={() => setWeekStart(addDays(weekStart, 7))}
                            className="p-2 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                            title="Settimana successiva"><ChevronRight size={18} /></button>
                        <button onClick={() => setWeekStart(startOfWeek(new Date()))}
                            className="px-3 py-2 text-sm font-bold rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50">Oggi</button>
                        <button onClick={onClose} className="p-2 rounded-lg text-gray-400 hover:text-gray-600 ml-2" title="Chiudi"><X size={20} /></button>
                    </div>
                </div>

                {/* Toolbar */}
                <div className="px-5 py-3 border-b border-gray-100 bg-white flex items-center justify-between gap-4 flex-wrap flex-shrink-0">
                    <div className="flex items-center gap-2 flex-1 min-w-[200px] max-w-md">
                        <Search size={16} className="text-gray-400" />
                        <input type="text" placeholder="Filtra collaboratore..." value={search}
                            onChange={e => setSearch(e.target.value)}
                            className="flex-1 outline-none text-sm px-2 py-1.5 border border-gray-200 rounded-lg" />
                    </div>
                    {/* Legenda */}
                    <div className="flex items-center gap-4 text-xs text-gray-600">
                        <span className="flex items-center gap-1.5"><span className="w-3 h-3 bg-indigo-500/85 border-l-2 border-indigo-700 rounded-sm" /> Investigazione</span>
                        <span className="flex items-center gap-1.5"><span className="w-3 h-3 bg-emerald-500/85 border-l-2 border-emerald-700 rounded-sm" /> Fiduciario</span>
                        <span className="flex items-center gap-1.5"><span className="w-3 h-3 bg-slate-300/70 border-l-2 border-slate-400 rounded-sm" /> Giorno intero</span>
                    </div>
                </div>

                {/* Griglia */}
                <div className="flex-1 overflow-auto">
                    {collaboratori.length === 0 ? (
                        <div className="text-center py-16 text-gray-400">
                            <CalendarDays size={36} className="mx-auto mb-3 opacity-30" />
                            <p className="font-semibold text-sm">Nessun collaboratore visibile.</p>
                        </div>
                    ) : (
                        <table className="min-w-full border-collapse">
                            <thead className="sticky top-0 bg-white z-10 shadow-sm">
                                <tr>
                                    <th className="text-left text-xs font-bold text-gray-500 uppercase tracking-wider px-4 py-3 border-b border-gray-200 sticky left-0 bg-white z-20 min-w-[180px]">Collaboratore</th>
                                    {weekDates.map((iso, idx) => {
                                        const d = new Date(iso);
                                        const isToday = iso === toIsoDate(new Date());
                                        return (
                                            <th key={iso}
                                                className={`text-center text-xs font-bold uppercase tracking-wider px-2 py-3 border-b border-l border-gray-200 min-w-[110px] ${isToday ? 'bg-blue-50 text-blue-700' : 'text-gray-500'}`}>
                                                <div>{DAY_NAMES[idx]}</div>
                                                <div className="text-[10px] font-normal opacity-70 mt-0.5">{String(d.getDate()).padStart(2, '0')}/{String(d.getMonth() + 1).padStart(2, '0')}</div>
                                            </th>
                                        );
                                    })}
                                    {onSelectCollaboratore && (
                                        <th className="text-center text-xs font-bold text-gray-500 uppercase tracking-wider px-3 py-3 border-b border-l border-gray-200 min-w-[110px]">Azione</th>
                                    )}
                                </tr>
                            </thead>
                            <tbody>
                                {collaboratori.map(u => {
                                    const dayBars = agendaByUser[u.id] || {};
                                    return (
                                        <tr key={u.id} className="hover:bg-slate-50/40">
                                            <td className="text-sm text-gray-800 px-4 py-2 border-b border-gray-100 sticky left-0 bg-white z-10 font-semibold align-top">
                                                <div className="flex items-center gap-2">
                                                    <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center text-xs font-bold text-gray-600 border border-gray-200 flex-shrink-0">
                                                        {(u.name || '?').charAt(0)}{(u.surname || '').charAt(0)}
                                                    </div>
                                                    <div className="min-w-0">
                                                        <div className="flex items-center gap-1.5 flex-wrap">
                                                            <span className="truncate">{u.name} {u.surname}</span>
                                                            {u.role === UserRole.ADMIN && (
                                                                <span className="text-[9px] font-black uppercase tracking-wider bg-red-100 text-red-700 border border-red-200 px-1.5 py-0.5 rounded-full">Titolare</span>
                                                            )}
                                                            {u.role === UserRole.RESPONSABILE && (
                                                                <span className="text-[9px] font-black uppercase tracking-wider bg-purple-100 text-purple-700 border border-purple-200 px-1.5 py-0.5 rounded-full">Resp.</span>
                                                            )}
                                                        </div>
                                                        <div className="text-[10px] text-gray-400 truncate">{u.email}</div>
                                                    </div>
                                                </div>
                                            </td>
                                            {weekDates.map(iso => (
                                                <td key={iso} className="p-0 border-b border-gray-100 align-top">
                                                    <DayCell bars={dayBars[iso] || []} onNavigate={goNavigate} />
                                                </td>
                                            ))}
                                            {onSelectCollaboratore && (
                                                <td className="p-2 border-b border-gray-100 align-middle text-center">
                                                    <button
                                                        type="button"
                                                        onClick={() => { onSelectCollaboratore(u.id); onClose(); }}
                                                        className="text-xs font-bold text-white bg-blue-600 hover:bg-blue-700 px-3 py-1.5 rounded-lg shadow-sm transition-colors"
                                                    >
                                                        Seleziona
                                                    </button>
                                                </td>
                                            )}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                    {loading && (
                        <div className="absolute inset-x-0 bottom-0 flex items-center justify-center py-2 bg-white/80 backdrop-blur-sm border-t border-gray-100">
                            <Loader2 size={14} className="animate-spin text-blue-500 mr-2" />
                            <span className="text-xs font-bold text-gray-500">Caricamento agenda...</span>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AgendaSettimanaleModal;
